import { createContext } from 'react';

export const TRANSLATION_PREFIX = 'STATIC.PAGES.CUSTOM_AI_TAGS';
export const FORM_PREFIX = `${TRANSLATION_PREFIX}.FORM`;

export const IMPROVE_BUTTON_TEXT = `${TRANSLATION_PREFIX}.AI_ACTIONS.IMPROVE`;
export const REVERT_BUTTON_TEXT = `${TRANSLATION_PREFIX}.AI_ACTIONS.REVERT`;
export const AUTO_SUGGEST_BUTTON_TEXT = `${TRANSLATION_PREFIX}.AI_ACTIONS.AUTO_SUGGEST`;

export const FORM_TRANSLATIONS = {
    DESCRIPTION_PLACEHOLDER: `${FORM_PREFIX}.DESCRIPTION_PLACEHOLDER`,
    DESCRIPTION_TITLE: `${FORM_PREFIX}.DESCRIPTION_TITLE`,
    DESCRIPTION_SUBTITLE: `${FORM_PREFIX}.DESCRIPTION_SUBTITLE`,
    APPS_PLACEHOLDER: `${FORM_PREFIX}.APPS_PLACEHOLDER`,
    APPS_TITLE: `${FORM_PREFIX}.APPS_TITLE`,
    APPS_SUBTITLE: `${FORM_PREFIX}.APPS_SUBTITLE`,
    RESULT_TYPE_TITLE: `${FORM_PREFIX}.RESULT_TYPE_TITLE`,
    RESULT_TYPE_TOOLTIP: `${FORM_PREFIX}.RESULT_TYPE_TOOLTIP`,
    RESULT_TYPE_SUBTITLE: `${FORM_PREFIX}.RESULT_TYPE_SUBTITLE`,
    HOOK_TITLE: `${FORM_PREFIX}.HOOK_TITLE`,
    HOOK_SUBTITLE: `${FORM_PREFIX}.HOOK_SUBTITLE`,
    HOOK_DURATION_LABEL: `${FORM_PREFIX}.HOOK_DURATION_LABEL`,
    HOOK_DURATION_SECONDS: `${FORM_PREFIX}.HOOK_DURATION_SECONDS`,
    HOOK_TAG_BADGE: `${FORM_PREFIX}.HOOK_TAG_BADGE`,
    HOOK_SCOPE_LABEL: `${FORM_PREFIX}.HOOK_SCOPE_LABEL`,
    HOOK_SCOPE_TEMPLATE: `${FORM_PREFIX}.HOOK_SCOPE_TEMPLATE`,
    CANCEL_BUTTON: `${FORM_PREFIX}.CANCEL_BUTTON`,
    CREATE_BUTTON: `${FORM_PREFIX}.CREATE_BUTTON`,
    UPDATE_BUTTON: `${FORM_PREFIX}.UPDATE_BUTTON`,
    VALUES_SUBTITLE: `${FORM_PREFIX}.VALUES_SUBTITLE`,
    VALUES_TITLE: `${FORM_PREFIX}.VALUES_TITLE`,
    VALUES_PLACEHOLDER: `${FORM_PREFIX}.VALUES_PLACEHOLDER`,
    ADD_VALUE_BUTTON: `${FORM_PREFIX}.ADD_VALUE_BUTTON`,
};

const STEP_CONFIGS = [
    { name: 'NAME', icon: 'file-text', hasBadge: false },
    { name: 'DESCRIPTION', icon: 'align-left', hasBadge: true },
    { name: 'TAGS', icon: 'tag', hasBadge: true },
];

const generateStepTranslations = () =>
    STEP_CONFIGS.reduce((acc, { name, hasBadge }) => {
        const suffixes = ['TITLE', 'SUBTITLE'];
        if (hasBadge) suffixes.push('BADGE');

        suffixes.forEach(suffix => {
            const key = `STEP_${name}_${suffix}`;
            acc[key] = `${TRANSLATION_PREFIX}.EMPTY_STATE.${key}`;
        });
        return acc;
    }, {});

export const EMPTY_STATE_TRANSLATIONS = {
    BADGE_LABEL: `${TRANSLATION_PREFIX}.EMPTY_STATE.BADGE_LABEL`,
    TITLE: `${TRANSLATION_PREFIX}.EMPTY_STATE.TITLE`,
    DESCRIPTION: `${TRANSLATION_PREFIX}.EMPTY_STATE.DESCRIPTION`,
    CREATE_LABEL: `${TRANSLATION_PREFIX}.EMPTY_STATE.CREATE_LABEL`,
    ...generateStepTranslations(),
};

export const EMPTY_STATE_STEPS = STEP_CONFIGS.map(({ name, icon, hasBadge }, index) => ({
    index: index + 1,
    title: EMPTY_STATE_TRANSLATIONS[`STEP_${name}_TITLE`],
    subtitle: EMPTY_STATE_TRANSLATIONS[`STEP_${name}_SUBTITLE`],
    icon,
    ...(hasBadge && {
        badge: {
            name: EMPTY_STATE_TRANSLATIONS[`STEP_${name}_BADGE`],
            icon: 'ai-line',
        },
    }),
}));

export const TOASTER_TRANSLATIONS = {
    DELETE_SUCCESS: `${TRANSLATION_PREFIX}.TOASTERS.DELETE_SUCCESS`,
    DELETE_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.DELETE_ERROR`,
    DUPLICATE_NAME_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.DUPLICATE_NAME_ERROR`,
    CREATE_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.CREATE_ERROR`,
    UPDATE_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.UPDATE_ERROR`,
    FETCH_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.FETCH_ERROR`,
    DUPLICATE_VALUE_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.DUPLICATE_VALUE_ERROR`,
    IMPROVE_MISSING_FIELDS: `${TRANSLATION_PREFIX}.TOASTERS.IMPROVE_MISSING_FIELDS`,
    IMPROVE_FIELD_ERROR: `${TRANSLATION_PREFIX}.TOASTERS.IMPROVE_FIELD_ERROR`,
};

export const PLAYGROUND_TRANSLATIONS = {
    TEST_BUTTON: `${FORM_PREFIX}.TEST_BUTTON`,
    POPUP_LOADING: `${TRANSLATION_PREFIX}.PLAYGROUND.LOADING`,
    POPUP_ERROR: `${TRANSLATION_PREFIX}.PLAYGROUND.ERROR`,
    POPUP_CLOSE: `${TRANSLATION_PREFIX}.PLAYGROUND.CLOSE`,
    POPUP_RETRY: `${TRANSLATION_PREFIX}.PLAYGROUND.RETRY`,
    NO_CREATIVES: `${TRANSLATION_PREFIX}.PLAYGROUND.NO_CREATIVES`,
    RESULT_ERROR: `${TRANSLATION_PREFIX}.PLAYGROUND.RESULT_ERROR`,
    LOAD_MORE: `${TRANSLATION_PREFIX}.PLAYGROUND.LOAD_MORE`,
    SOURCE_FILTER_PLACEHOLDER: `${TRANSLATION_PREFIX}.PLAYGROUND.SOURCE_FILTER_PLACEHOLDER`,
};

export const VALUE_TYPES = {
    SINGLE: 0,
    MULTIPLE: 1,
};

// Keep in sync with HOOK_DURATION_OPTIONS in django/dashboard/creatives_ai/consts.py.
export const HOOK_DURATION_OPTIONS = [3, 5, 8, 10, 15];

export const HOOK_DURATION_LIMITS = {
    DEFAULT: 5,
    OPTIONS: HOOK_DURATION_OPTIONS,
};

export const getHookDurationOptions = translate =>
    HOOK_DURATION_OPTIONS.map(value => ({
        label: translate(FORM_TRANSLATIONS.HOOK_DURATION_SECONDS, { duration: value }),
        value,
    }));

export const EMPTY_DIMENSION = {
    id: null,
    display_name: '',
    description: '',
    options: [],
    value_type: VALUE_TYPES.SINGLE,
    media_type: null,
    category: null,
    apps: null,
    hook_enabled: false,
    hook_duration: HOOK_DURATION_LIMITS.DEFAULT,
};

export const VALUE_TYPE_LABELS = {
    [VALUE_TYPES.SINGLE]: 'Single',
    [VALUE_TYPES.MULTIPLE]: 'Multiple',
};

export const getValueTypeOptions = () =>
    Object.entries(VALUE_TYPE_LABELS).map(([value, label]) => ({
        option: label.toLowerCase(),
        label,
        value: Number(value),
    }));

export const FORM_FIELDS = {
    APPS: 'apps',
    DESCRIPTION: 'description',
    VALUE_TYPE: 'valueType',
    OPTIONS: 'options',
    HOOK_ENABLED: 'hookEnabled',
    HOOK_DURATION: 'hookDuration',
};

export const MAX_VALUES_LIMIT = 25;
export const MAX_OPTIONS_LENGTH = 30;
export const DIMENSIONS_LIMIT = 30;
export const POPUP_DELAY = 5000;

export const CustomAIDimensionContext = createContext();

export const MIXPANEL = {
    EVENT_PREFIX: 'Manage AI Dimensions',
    ACTIONS: {
        SAVE_NEW: 'Add Dimension',
        EDIT: 'Edit Dimension',
        DELETE: 'Delete Dimension',
        TEST_DIMENSION: 'Test Dimension',
        LOAD_MORE_CREATIVES: 'Load More Creatives',
        FILTER_SOURCE: 'Filter Source',
    },
    APP_VALUES: {
        ALL_APPS: 'all apps',
        SPECIFIC_APP: 'specific app',
    },
};

export const buildMixpanelPayload = (dimension, appsList = []) => {
    const isAllApps = !dimension.apps?.length;
    const selectedAppNames = isAllApps
        ? []
        : appsList.filter(app => dimension.apps?.includes(app.value)).map(app => app.label);

    return {
        dimension_name: dimension.display_name,
        result_type: VALUE_TYPE_LABELS[dimension.value_type],
        value_list: dimension.options?.length > 0,
        app: isAllApps ? MIXPANEL.APP_VALUES.ALL_APPS : MIXPANEL.APP_VALUES.SPECIFIC_APP,
        app_selected: selectedAppNames,
        is_hook_tag: !!dimension.hook_enabled,
        hook_duration: dimension.hook_enabled ? dimension.hook_duration : null,
    };
};
